import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { Spinner } from '@/components/Spinner';
import { Page } from '@/components/layout/Page';
import { ExpandButton, useChartExpand } from '@/features/data/products/ChartModal';
import {
  CAL_VARIATIONS,
  Raw,
  calHtml,
  distHtml,
  hashKey,
  histDist,
  introBox,
  intervalHtml,
  qtyHtml,
  type Distribution,
} from '@/features/data/shared/charts';
import { C, cv } from '@/features/data/shared/theme';

/* ── Types ────────────────────────────────────────────────────────────── */
type Behavior = { name: string; val: string; conf: number; color: string };
type LifecycleStage = { stage: string; p50: string; health: string; note: string };
type Pattern = { name: string; conf: number; color: string; desc: string };
type Economic = {
  revQ: number[];
  revLabels: string[];
  revenueAtRisk: string;
  openOrderValue: string;
  margin: string;
  forecastErrorCost: string;
  customers: number;
  skuRank: string;
};
type Order = {
  id: string;
  customer: string;
  date: string;
  qty: string;
  status: string;
  commit: string;
  risk: string;
  riskLabel: string;
};
type CustomerMix = {
  custId?: string;
  name: string;
  type: string | null;
  memory: number | null;
  risk: string | null;
  riskLabel: string | null;
};
type Product = {
  id: string;
  name: string;
  sku: string;
  type: string;
  source: string;
  forecastability: string;
  forecastScore: number;
  risk: string;
  riskLabel: string;
  demandTrend: string;
  revYTD: string;
  lastOrderIn: string;
  avgOrderQty: string;
  ordersLTM: number;
  openOrders: number;
  nextDelivery: string;
  interarrival: string;
  qtyVariance: string;
  sig: string;
  customers: string[];
  behaviors: Behavior[];
  lifecycle: LifecycleStage[];
  patterns: Pattern[];
  economic: Economic;
  orders: { open: Order[]; closed: Order[] };
  demandCharts: { interarrival: number[]; quantity: number[] };
  customerMix: CustomerMix[];
  profile?: ProfileData;
  mem?: { calendarData: number[][]; distributions: Distribution[] };
};
// Behavioral Profile + stats/upcoming + AI Readiness mock template (absorbed from the Objects page; cycled).
type ProfileData = {
  metrics: { lbl: string; val: string; sub: string }[];
  intel: { lbl: string; val: string; sub: string; signal: string; sc: string }[];
  upcoming: { dist: string; name: string; source: string; impact: string; dir: string }[];
  encodings: { name: string; pct: number }[];
  dimensions: [string, string][];
  readiness: { icon: string; ok: boolean; dim: string; detail: string }[];
  pattern: string;
  patternConf: string;
  aiReady: boolean;
};
// Real identity from GET /api/data-app/products/:id (describe).
type ProductDescribe = {
  id: string;
  name: string;
  businessGroup: string;
  productLine: string;
  productClass: string;
  baseUom: string;
  saleUom: string;
  status: string;
};
// Master/detail order history from GET /api/data-app/product/:id/orders — customer-centric.
type IntelCell = { l: string; v: string; s: string };
type EventRec = { type: string; date: string; cat: string; note: string; risk: boolean; color?: string };
type POrderRow = {
  id: string;
  custId: string;
  customer: string;
  date: string;
  status: string;
  commit: string;
  risk: string;
  riskLabel: string;
};
type CustomerRef = { custId: string; name: string };
// GET /api/data-app/product/:id/orders — order rows + derived real summary (customers / count / latest date).
type ProductOrdersData = {
  past: POrderRow[];
  customers: CustomerRef[];
  ordersLTM: number;
  lastOrderIn: string;
};
// Full order (ALL line items across products) from GET /api/data-app/order/:orderId — same shape the
// Customers page uses. The Products Orders tab reads line items from here so the count matches.
type FullLineItem = {
  id: string;
  product: string;
  productId: string;
  qty: string;
  rdd: string;
  commit: string;
  risk: string;
  riskLabel: string;
  intel: IntelCell[];
  events: EventRec[];
};
type FullOrder = { product: string; qty: string; orderDate: string; commitDate: string; lineItems: FullLineItem[] };
// Real aggregated demand behavior from GET /api/data-app/product/:id/behavior (event-behavior API).
type BehaviorData = {
  interarrival: number[];
  quantity: number[];
  dow: { days: string[]; dots: Record<string, number[]> };
  interarrivalMode: string;
  interarrivalP50: number;
  interarrivalP90: number;
  quantityAvg: string;
  quantityStd: string;
  dowMode: string;
  commitDriftMedian: string;
  orders: number;
  historyMonths: number;
  scope: 'overall' | 'pair';
  pairsUsed: number;
  customersTotal?: number;
};

/* ── Color helpers (page-specific; shared C/VAR/cv live in ./shared/charts) ── */
const riskColor = (r: string | null) => (r === 'ok' ? C.pos : r === 'warn' ? C.warn : r === 'risk' ? C.neg : C.t3);
const riskBg = (r: string | null) =>
  r === 'ok'
    ? 'rgba(42,128,85,.1)'
    : r === 'warn'
      ? 'rgba(210,122,36,.1)'
      : r === 'risk'
        ? 'rgba(188,46,61,.1)'
        : 'rgba(146,149,165,.1)';
const scoreColor = (s: number) => (s >= 80 ? C.pos : s >= 60 ? C.warn : C.neg);

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  overflow: 'hidden',
};
function dowHtml(dow: { days: string[]; dots: Record<string, number[]> }, modeDay = '') {
  const w = 340,
    rh = 13,
    pad = 26,
    top = 4;
  // viewBox height fits all rows (was a fixed 58 that clipped Fri–Sun); height:auto + meet keep it centered/zoomable.
  const h = top + dow.days.length * rh + 4;
  return (
    `<svg viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" style="width:100%;height:auto">` +
    `<line x1="${pad}" y1="0" x2="${pad}" y2="${h}" stroke="#E4E0D8" stroke-width="0.5"/>` +
    dow.days
      .map((d, i) => {
        const yc = top + i * rh + rh / 2;
        return (
          `<text x="${pad - 2}" y="${yc + 3}" font-size="7.5" fill="#9295A5" text-anchor="end" font-family="Inter,sans-serif">${d}</text>` +
          (dow.dots[d] || [])
            .map(
              (n) =>
                `<circle cx="${(pad + n * ((w - pad - 6) / 46)).toFixed(1)}" cy="${yc.toFixed(1)}" r="3" fill="${d === modeDay ? '#274483' : '#91A2C0'}"/>`,
            )
            .join('')
        );
      })
      .join('') +
    '</svg>'
  );
}

/* ── Summary tab ──────────────────────────────────────────────────────── */
function SummaryTab({ p, describe }: { p: Product; describe?: ProductDescribe }) {
  // Real taxonomy/UoM/status from the describe API, surfaced above the template facts.
  const realFacts = [
    describe?.businessGroup && {
      l: 'Category',
      v: describe.businessGroup,
      s: describe.productLine || 'Business group',
    },
    describe?.baseUom && {
      l: 'Unit of Measure',
      v:
        describe.saleUom && describe.saleUom !== describe.baseUom
          ? `${describe.baseUom} · sold as ${describe.saleUom}`
          : describe.baseUom,
      s: 'Base / sale UoM · SAP MM',
    },
    describe?.status && { l: 'Status', v: describe.status, s: 'Product master · SAP MM' },
  ].filter(Boolean) as { l: string; v: string; s: string }[];
  const facts = [
    { l: 'SKU', v: p.id, s: 'Product ID · SAP MM' },
    ...realFacts,
    { l: 'Last Order In', v: p.lastOrderIn, s: 'Most recent demand signal' },
    { l: 'Open Orders', v: p.openOrders + ' orders', s: 'Currently active' },
    { l: 'Revenue YTD', v: p.revYTD, s: p.demandTrend + ' vs prior year' },
  ];
  // Insight quote: inject the SKU's real top customer names into the templatized mock copy.
  const sig = p.sig
    .replace(/\{customer2\}/g, p.customers[1] || p.customers[0] || 'another key account')
    .replace(/\{customer\}/g, p.customers[0] || 'this account');
  const trends = [
    { l: 'Demand Trend', v: p.demandTrend, pos: p.demandTrend.startsWith('+') },
    {
      l: 'Forecastability',
      v: p.forecastability,
      pos: p.forecastability === 'Very High' || p.forecastability === 'High',
    },
    { l: 'Interarrival (avg)', v: p.interarrival, pos: true },
    { l: 'Qty Variance', v: p.qtyVariance, pos: p.qtyVariance === 'Low' },
    { l: 'Forecast Score', v: p.forecastScore + '%', pos: p.forecastScore >= 70 },
  ];
  return (
    <div>
      <ProfileStats profile={p.profile} />
      <UpcomingSignals profile={p.profile} />
      <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Product Facts</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>Source: SAP MM · MARA/MARC · As of today</div>
          </div>
          <div style={{ padding: '10px 14px' }}>
            {facts.map((m, i) => (
              <div
                key={m.l}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '7px 0',
                  borderTop: i > 0 ? `1px solid ${C.border}` : undefined,
                }}
              >
                <div style={{ fontSize: 11, color: C.t2 }}>{m.l}</div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: C.t1 }}>{m.v}</div>
                  <div style={{ fontSize: 9.5, color: C.t3 }}>{m.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Demand Trends</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>What Keystone sees in the demand trajectory</div>
          </div>
          <div style={{ padding: '10px 14px' }}>
            {trends.map((m, i) => (
              <div
                key={m.l}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '7px 0',
                  borderTop: i > 0 ? `1px solid ${C.border}` : undefined,
                }}
              >
                <div style={{ fontSize: 11, color: C.t2 }}>{m.l}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: m.pos ? C.pos : C.neg }}>{m.v}</div>
              </div>
            ))}
            <div
              style={{
                marginTop: 8,
                padding: '9px 12px',
                background: 'rgba(39,68,131,.05)',
                borderRadius: 6,
                borderLeft: `3px solid ${C.blue}`,
              }}
            >
              <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.6 }}>{sig}</div>
            </div>
          </div>
        </div>
      </div>
      <PatternPackMatch profile={p.profile} />
    </div>
  );
}

/* ── Absorbed-from-Objects shared bits: stats bar · upcoming · behavioral profile · readiness ──────────── */
const PROFILE_LABEL: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  color: '#9295A5',
  textTransform: 'uppercase',
  letterSpacing: '.1em',
};
function ProfileStats({ profile }: { profile?: ProfileData }) {
  if (!profile) return null;
  const cells = [...profile.intel.slice(0, 2), ...profile.metrics];
  if (!cells.length) return null;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cells.length},1fr)`,
        borderBottom: `1px solid ${C.border}`,
        background: '#fff',
      }}
    >
      {cells.map((c, i) => {
        const isFwd = i < profile.intel.slice(0, 2).length;
        const fwd = c as { lbl: string; val: string; sub: string; signal?: string; sc?: string };
        return (
          <div
            key={i}
            style={{
              padding: '9px 12px',
              borderRight: i === cells.length - 1 ? 'none' : `1px solid ${C.border}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              background: isFwd ? 'rgba(39,68,131,0.02)' : undefined,
            }}
          >
            <div
              style={{
                fontSize: 8,
                fontWeight: 700,
                color: isFwd ? C.blue : C.t3,
                textTransform: 'uppercase',
                letterSpacing: '.08em',
              }}
            >
              {c.lbl}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              {c.val}
            </div>
            <div style={{ fontSize: 9, color: C.t3, lineHeight: 1.3 }}>{c.sub}</div>
            {isFwd && fwd.signal ? (
              <div style={{ fontSize: 9, fontWeight: 600, color: cv(fwd.sc ?? ''), marginTop: 1 }}>{fwd.signal}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
function UpcomingSignals({ profile }: { profile?: ProfileData }) {
  if (!profile?.upcoming?.length) return null;
  const dirColor = (d: string) => (d === 'up' ? C.pos : d === 'dn' ? C.neg : C.warn);
  return (
    <div style={{ padding: '10px 20px', borderBottom: `1px solid ${C.border}`, background: '#fff' }}>
      <div style={{ ...PROFILE_LABEL, marginBottom: 7 }}>Upcoming · Historical Signals</div>
      {profile.upcoming.map((u, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '5px 0',
            borderBottom: i === profile.upcoming.length - 1 ? 'none' : `1px solid ${C.border}`,
          }}
        >
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 700,
              padding: '2px 7px',
              borderRadius: 100,
              background: '#fff',
              color: C.t2,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              border: `1px solid ${C.border}`,
            }}
          >
            {u.dist}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11.5, color: C.t1, fontWeight: 500 }}>{u.name}</div>
            <div style={{ fontSize: 9.5, color: C.t3 }}>{u.source}</div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', color: dirColor(u.dir) }}>
            {u.impact}
          </span>
        </div>
      ))}
    </div>
  );
}
// Pattern Pack Match — stays on the Summary tab (no event-behavior dependency).
function PatternPackMatch({ profile }: { profile?: ProfileData }) {
  if (!profile) return null;
  return (
    <div style={{ padding: '16px 20px', borderTop: `1px solid ${C.border}` }}>
      <div style={{ ...PROFILE_LABEL, marginBottom: 8 }}>Pattern Pack Match</div>
      <div style={{ background: C.warm, borderRadius: 8, padding: '10px 12px', borderLeft: `3px solid ${C.blue}` }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: C.t1, marginBottom: 2 }}>{profile.pattern}</div>
        <div style={{ fontSize: 10.5, color: C.t3 }}>{profile.patternConf} match confidence</div>
      </div>
    </div>
  );
}
// Behavioral Encodings + Behavioral Dimensions — moved to the Demand Behavior tab (live event-behavior values).
function BehavioralEncodingsDims({ profile, bh }: { profile?: ProfileData; bh?: BehaviorData }) {
  if (!profile) return null;
  const encColor = (n: number) => (n >= 80 ? C.blue : n >= 60 ? C.warn : C.neg);
  const encT = (n: number) => (n >= 80 ? C.t1 : n >= 60 ? C.warn : C.neg);
  const realDims: Record<string, string> = bh
    ? {
        'Interarrival P50': bh.interarrivalP50 ? `${bh.interarrivalP50} days` : '',
        'Interarrival P90': bh.interarrivalP90 ? `${bh.interarrivalP90} days` : '',
        'Most Common Order Day': bh.dowMode,
        'Avg Order Quantity': bh.quantityAvg === '—' ? '' : `${bh.quantityAvg} units`,
        'Quantity Std Dev': bh.quantityStd === '—' ? '' : `${bh.quantityStd} units`,
        'Commit Drift Median': bh.commitDriftMedian === '—' ? '' : bh.commitDriftMedian,
        'History Depth': bh.historyMonths ? `${bh.historyMonths} months` : '',
      }
    : {};
  const dims: [string, string][] = profile.dimensions.map(([k, v]) => [k, realDims[k] ? realDims[k] : v]);
  const anyLive = Object.values(realDims).some(Boolean);
  return (
    <div
      style={{
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <div>
        <div style={{ ...PROFILE_LABEL, marginBottom: 8 }}>Behavioral Encodings</div>
        {profile.encodings.map((e) => (
          <div
            key={e.name}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 0',
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <div style={{ fontSize: 11.5, color: C.t1, flex: 1 }}>{e.name}</div>
            <div style={{ width: 60, height: 4, borderRadius: 2, background: C.border, flexShrink: 0 }}>
              <div style={{ width: `${e.pct}%`, height: 4, borderRadius: 2, background: encColor(e.pct) }} />
            </div>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                color: encT(e.pct),
                width: 30,
                textAlign: 'right',
                flexShrink: 0,
              }}
            >
              {e.pct}%
            </div>
          </div>
        ))}
      </div>
      <div>
        <div style={{ ...PROFILE_LABEL, marginBottom: 8 }}>
          Behavioral Dimensions{anyLive ? ' · live values from event-behavior API' : ''}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', marginTop: 6 }}>
          {dims.map((d, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                padding: '4px 0',
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <span style={{ fontSize: 10.5, color: C.t3 }}>{d[0]}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{d[1]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
/* ── Orders tab (master/detail; customer-centric) ─────────────────────────
   Mirrors the Customers Orders tab, but the line-item detail shows the linked
   CUSTOMER (id / name / events) instead of the product. */
function OrdersTab({ p, productId }: { p: Product; productId: string }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [activeSo, setActiveSo] = useState<string | null>(null);
  const [activeLi, setActiveLi] = useState<string | null>(null);
  const [liPage, setLiPage] = useState(1);
  const ORD_PAGE_SIZE = 10;
  const LI_PAGE_SIZE = 10;

  // Order list = orders that contain THIS product (product_id-filtered, cached).
  const { data: listData, isLoading: listLoading } = useQuery<ProductOrdersData>({
    queryKey: ['data-app', 'product', productId, 'orders'],
    queryFn: async () => {
      const r = await fetch(`/api/data-app/product/${encodeURIComponent(productId)}/orders`);
      if (!r.ok) throw new Error('Failed to load product orders');
      return r.json();
    },
    enabled: !!productId,
    staleTime: 5 * 60 * 1000,
  });
  // Line items for the SELECTED order = the FULL order (every line item across all products), via the shared
  // per-order endpoint — so the count matches what the Customers page shows for the same order.
  const { data: liveOrder, isLoading: orderLoading } = useQuery<FullOrder>({
    // Secondary product_id filter (on top of order_id) → only THIS SKU's line items for the order.
    queryKey: ['data-app', 'order', activeSo, productId],
    queryFn: async () => {
      const r = await fetch(
        `/api/data-app/order/${encodeURIComponent(activeSo!)}?product=${encodeURIComponent(productId)}`,
      );
      if (!r.ok) throw new Error('Failed to load order line items');
      return r.json();
    },
    enabled: !!activeSo,
    staleTime: 5 * 60 * 1000,
  });

  const past = useMemo(() => listData?.past ?? [], [listData]);
  const q = search.toLowerCase();
  const filtered = past.filter(
    (o) =>
      !q ||
      o.id.toLowerCase().includes(q) ||
      o.customer.toLowerCase().includes(q) ||
      o.custId.toLowerCase().includes(q),
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / ORD_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = filtered.slice((safePage - 1) * ORD_PAGE_SIZE, safePage * ORD_PAGE_SIZE);

  const selectSo = (id: string) => {
    setActiveSo(id);
    setActiveLi(null);
    setLiPage(1);
  };
  const activeRow = activeSo ? (past.find((o) => o.id === activeSo) ?? null) : null;
  const lineItems = liveOrder?.lineItems ?? [];
  // Auto-select the first line item of the active order (no setState needed — derived).
  const effectiveLi = activeLi ?? lineItems[0]?.id ?? null;
  const li = effectiveLi ? (lineItems.find((x) => x.id === effectiveLi) ?? null) : null;
  const custHref = (id: string) => `/data/customers?id=${encodeURIComponent(id)}`;

  if (listLoading) {
    return <div style={{ padding: 20, fontSize: 11, color: C.t3 }}>Loading orders for {p.name}…</div>;
  }

  // Inline line items for the active order (the full order, paginated 10/page).
  const renderLineItems = () => {
    if (orderLoading) {
      return <div style={{ padding: '10px 12px 10px 40px', fontSize: 10, color: C.t3 }}>Loading line items…</div>;
    }
    if (!lineItems.length) {
      return <div style={{ padding: '10px 12px 10px 40px', fontSize: 10, color: C.t3 }}>No line items.</div>;
    }
    const liPageCount = Math.max(1, Math.ceil(lineItems.length / LI_PAGE_SIZE));
    const liSafe = Math.min(liPage, liPageCount);
    const shown = lineItems.slice((liSafe - 1) * LI_PAGE_SIZE, liSafe * LI_PAGE_SIZE);
    return (
      <div>
        {shown.map((item) => (
          <div
            key={item.id}
            onClick={() => setActiveLi(item.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px 8px 40px',
              borderBottom: `1px solid ${C.border}`,
              cursor: 'pointer',
              background: effectiveLi === item.id ? 'rgba(39,68,131,.07)' : C.warm,
              borderLeft: `3px solid rgba(42,128,85,.3)`,
            }}
          >
            <div style={{ flex: 1 }}>
              {/* product is fixed for this SKU + shown in the detail pane, so just the line-item id here */}
              <span style={{ fontSize: 10, fontWeight: 700, color: C.blue, fontFamily: 'monospace' }}>{item.id}</span>
            </div>
          </div>
        ))}
        {liPageCount > 1 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 12px 6px 40px',
              borderBottom: `1px solid ${C.border}`,
              background: C.warm,
            }}
          >
            <button
              onClick={() => setLiPage((pg) => Math.max(1, pg - 1))}
              disabled={liSafe <= 1}
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: liSafe <= 1 ? C.t3 : C.blue,
                background: 'none',
                border: 'none',
                cursor: liSafe <= 1 ? 'default' : 'pointer',
                padding: 0,
              }}
            >
              ‹ Prev
            </button>
            <span style={{ fontSize: 9.5, color: C.t3 }}>
              {liSafe} / {liPageCount}
            </span>
            <button
              onClick={() => setLiPage((pg) => Math.min(liPageCount, pg + 1))}
              disabled={liSafe >= liPageCount}
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: liSafe >= liPageCount ? C.t3 : C.blue,
                background: 'none',
                border: 'none',
                cursor: liSafe >= liPageCount ? 'default' : 'pointer',
                padding: 0,
              }}
            >
              Next ›
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', height: '100%' }}>
      {/* left: order list */}
      <div
        style={{
          width: 250,
          flexShrink: 0,
          borderRight: `1px solid ${C.border}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: C.warm,
        }}
      >
        <div
          style={{ padding: '10px 10px 8px', borderBottom: `1px solid ${C.border}`, background: '#fff', flexShrink: 0 }}
        >
          <div style={{ fontSize: 10, fontWeight: 700, color: C.t2, marginBottom: 6 }}>
            Orders for this SKU · {filtered.length} across all customers
          </div>
          <input
            type="text"
            placeholder="Search order # or customer..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{
              width: '100%',
              padding: '5px 9px',
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              fontSize: 11,
              color: C.t1,
              background: C.warm,
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        {pageCount > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 12px',
              borderBottom: `1px solid ${C.border}`,
              background: '#fff',
            }}
          >
            <button
              onClick={() => setPage((pg) => Math.max(1, pg - 1))}
              disabled={safePage <= 1}
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                color: safePage <= 1 ? C.t3 : C.blue,
                background: 'none',
                border: 'none',
                cursor: safePage <= 1 ? 'default' : 'pointer',
                padding: 0,
              }}
            >
              ‹ Prev
            </button>
            <span style={{ fontSize: 10, color: C.t3 }}>
              {safePage} / {pageCount}
            </span>
            <button
              onClick={() => setPage((pg) => Math.min(pageCount, pg + 1))}
              disabled={safePage >= pageCount}
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                color: safePage >= pageCount ? C.t3 : C.blue,
                background: 'none',
                border: 'none',
                cursor: safePage >= pageCount ? 'default' : 'pointer',
                padding: 0,
              }}
            >
              Next ›
            </button>
          </div>
        )}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {paged.map((o) => {
            const isActive = activeSo === o.id;
            return (
              <div key={o.id}>
                <div
                  style={{
                    borderLeft: `3px solid rgba(42,128,85,.3)`,
                    background: isActive ? 'rgba(39,68,131,.05)' : '#fff',
                    borderBottom: `1px solid ${C.border}`,
                    padding: '8px 12px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isActive) setActiveSo(null);
                        else selectSo(o.id);
                      }}
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 3,
                        border: `1px solid ${C.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        color: C.t3,
                        cursor: 'pointer',
                        flexShrink: 0,
                        lineHeight: 1,
                        userSelect: 'none',
                      }}
                    >
                      {isActive ? '−' : '+'}
                    </div>
                    <div onClick={() => selectSo(o.id)} style={{ flex: 1, cursor: 'pointer' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: 3,
                        }}
                      >
                        <span style={{ fontSize: 11, fontWeight: 700, color: C.blue, fontFamily: 'monospace' }}>
                          {o.id}
                        </span>
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: '1px 7px',
                            borderRadius: 100,
                            background: riskBg(o.risk),
                            color: riskColor(o.risk),
                          }}
                        >
                          {o.riskLabel}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 2 }}>{o.customer}</div>
                      {/* Once opened, show the live (complete) order's earliest event date for this SKU. */}
                      <div style={{ fontSize: 10, color: C.t3 }}>
                        {o.id === activeSo && liveOrder ? liveOrder.orderDate : o.date}
                      </div>
                    </div>
                  </div>
                </div>
                {isActive ? renderLineItems() : null}
              </div>
            );
          })}
          {!paged.length && <div style={{ padding: '12px', fontSize: 10.5, color: C.t3 }}>No orders found.</div>}
        </div>
      </div>

      {/* right: detail */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!activeRow ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              color: C.t3,
            }}
          >
            <div style={{ fontSize: 32, opacity: 0.15 }}>📋</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.t2 }}>Select an order</div>
            <div style={{ fontSize: 11 }}>Click any order to see its line items and the customer timeline</div>
          </div>
        ) : (
          <>
            <div
              style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`, background: '#fff', flexShrink: 0 }}
            >
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: C.t3,
                  textTransform: 'uppercase',
                  letterSpacing: '.08em',
                  marginBottom: 3,
                }}
              >
                Sales Order · SAP SD · VBAK/VBAP
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: C.t1,
                  fontFamily: 'monospace',
                  letterSpacing: '.02em',
                  marginBottom: 2,
                }}
              >
                {activeSo}
              </div>
              <div style={{ fontSize: 10.5, color: C.t3 }}>
                <Link
                  to={custHref(activeRow.custId)}
                  style={{ color: C.blue, fontWeight: 700, textDecoration: 'none' }}
                >
                  {activeRow.customer}
                </Link>
                {liveOrder?.orderDate || activeRow.date ? ' · Ordered ' + (liveOrder?.orderDate || activeRow.date) : ''}
                {orderLoading ? '' : ` · ${lineItems.length} line item${lineItems.length === 1 ? '' : 's'}`}
              </div>
            </div>
            {!li ? (
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 6,
                  color: C.t3,
                }}
              >
                <div style={{ fontSize: 22, opacity: 0.15 }}>⚡</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.t2 }}>Select a line item</div>
                <div style={{ fontSize: 11 }}>Click a line item to see the customer and event timeline</div>
              </div>
            ) : (
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <div
                  style={{
                    padding: '10px 18px',
                    borderBottom: `1px solid ${C.border}`,
                    background: C.warm,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.t3,
                      textTransform: 'uppercase',
                      letterSpacing: '.08em',
                      marginBottom: 2,
                    }}
                  >
                    Line Item · SAP SD · VBAP
                  </div>
                  <div
                    style={{ fontSize: 13, fontWeight: 700, color: C.blue, fontFamily: 'monospace', marginBottom: 4 }}
                  >
                    {li.id}
                  </div>
                  {/* linked customer (replaces the product shown on the Customers page) */}
                  <div
                    style={{
                      fontSize: 8,
                      fontWeight: 700,
                      color: C.t3,
                      textTransform: 'uppercase',
                      letterSpacing: '.08em',
                      marginBottom: 2,
                    }}
                  >
                    Ship-to Customer
                  </div>
                  <Link
                    to={custHref(activeRow.custId)}
                    style={{ fontSize: 12, fontWeight: 700, color: C.blue, textDecoration: 'none' }}
                  >
                    {activeRow.customer}
                  </Link>
                  {li.rdd ? <div style={{ fontSize: 10, color: C.t3, marginTop: 6 }}>RDD {li.rdd}</div> : null}
                </div>
                {/* intel bar */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4,1fr)',
                    borderBottom: `1px solid ${C.border}`,
                    background: '#fff',
                  }}
                >
                  {li.intel.map((cell, i) => (
                    <div
                      key={cell.l}
                      style={{ padding: '10px 14px', borderRight: i < 3 ? `1px solid ${C.border}` : undefined }}
                    >
                      <div
                        style={{
                          fontSize: 8,
                          fontWeight: 700,
                          color: C.t3,
                          textTransform: 'uppercase',
                          letterSpacing: '.08em',
                          marginBottom: 3,
                        }}
                      >
                        {cell.l}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, marginBottom: 2 }}>{cell.v}</div>
                      <div style={{ fontSize: 9.5, color: C.t3 }}>{cell.s}</div>
                    </div>
                  ))}
                </div>
                {/* timeline */}
                <div style={{ padding: '16px 18px' }}>
                  <div
                    style={{
                      fontSize: 8.5,
                      fontWeight: 700,
                      color: C.t3,
                      textTransform: 'uppercase',
                      letterSpacing: '.1em',
                      marginBottom: 14,
                    }}
                  >
                    Event Timeline · {li.id} · Source: SAP CDHDR/CDPOS
                  </div>
                  {li.events.map((e, i) => {
                    const isSignal = e.cat === 'Signal';
                    // Per-event-type color from the shared backend config (consistent across pages).
                    const dotColor = e.color || (e.risk ? C.neg : isSignal ? C.pos : C.blue);
                    const dotBg = e.color
                      ? `${e.color}22`
                      : e.risk
                        ? 'rgba(188,46,61,.08)'
                        : isSignal
                          ? 'rgba(42,128,85,.06)'
                          : 'rgba(39,68,131,.06)';
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          gap: 14,
                          padding: '12px 0',
                          borderTop: i > 0 ? `1px solid ${C.border}` : undefined,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flexShrink: 0,
                            width: 20,
                          }}
                        >
                          <div
                            style={{
                              width: 11,
                              height: 11,
                              borderRadius: '50%',
                              background: dotColor,
                              boxShadow: `0 0 0 3px ${dotBg}`,
                              marginTop: 3,
                              flexShrink: 0,
                            }}
                          />
                          {i < li.events.length - 1 && (
                            <div style={{ width: 1.5, flex: 1, background: C.border, marginTop: 5, minHeight: 14 }} />
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}
                          >
                            <span style={{ fontSize: 12.5, fontWeight: 700, color: e.risk ? C.neg : C.t1 }}>
                              {e.type}
                            </span>
                            <span
                              style={{
                                fontSize: 8.5,
                                fontWeight: 700,
                                padding: '2px 7px',
                                borderRadius: 100,
                                background: isSignal ? 'rgba(42,128,85,.1)' : 'rgba(39,68,131,.1)',
                                color: isSignal ? C.pos : C.blue,
                              }}
                            >
                              {e.cat}
                            </span>
                          </div>
                          <div style={{ fontSize: 10, color: C.t3 }}>{e.date}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Demand Behavior tab ──────────────────────────────────────────────── */
function DemandTab({ p }: { p: Product }) {
  const { open, node } = useChartExpand();
  const [mixPage, setMixPage] = useState(1);
  const MIX_PAGE_SIZE = 10;
  const mixPageCount = Math.max(1, Math.ceil(p.customerMix.length / MIX_PAGE_SIZE));
  const mixSafe = Math.min(mixPage, mixPageCount);
  const mixShown = p.customerMix.slice((mixSafe - 1) * MIX_PAGE_SIZE, mixSafe * MIX_PAGE_SIZE);
  // Behavior scope — Overall (aggregated across the SKU's customers) vs a specific product×customer pair.
  const [scope, setScope] = useState<'overall' | 'pair'>('overall');
  const [pairCust, setPairCust] = useState('');
  const custOpts = useMemo(() => {
    const m = new Map<string, string>();
    for (const cm of p.customerMix) if (cm.custId) m.set(cm.custId, cm.name);
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [p.customerMix]);
  const pairActive = scope === 'pair' && !!pairCust;
  const { data: bh } = useQuery<BehaviorData>({
    queryKey: ['data-app', 'product', p.id, 'behavior', pairActive, pairCust],
    queryFn: async () => {
      const url = pairActive
        ? `/api/data-app/product/${encodeURIComponent(p.id)}/behavior?cust=${encodeURIComponent(pairCust)}`
        : `/api/data-app/product/${encodeURIComponent(p.id)}/behavior`;
      const r = await fetch(url);
      if (!r.ok) throw new Error('Failed to load behavior');
      return r.json();
    },
    enabled: !!p.id && (scope === 'overall' || !!pairCust),
    staleTime: 5 * 60 * 1000,
  });
  const realIa = bh?.interarrival?.length ? bh.interarrival : null;
  const realQty = bh?.quantity?.length ? bh.quantity : null;
  // No event-behavior data at all (loaded, zero orders) — show a clear message instead of mock-fallback charts.
  const noBehavior = !!bh && (bh.orders ?? 0) === 0;
  const pairCustName = custOpts.find((o) => o.id === pairCust)?.name ?? pairCust;
  const scopeNote = pairActive
    ? `this SKU × ${pairCustName} · ${bh?.orders ?? 0} orders`
    : `Overall Demand Behavior · ${bh?.orders ?? 0} orders`;

  const interTitle = 'Time Between Orders — Last 45 Demand Events';
  const interSub = realIa
    ? `Mode ${bh?.interarrivalMode || '—'}d · P50 ${bh?.interarrivalP50}d · P90 ${bh?.interarrivalP90}d`
    : 'Source: VBAK.ERDAT · Aggregated across all customers · Feeds product interarrival prior in BFG';
  const qtyTitle = 'Order Quantity — Last 45 Demand Events';
  const qtySub = realQty
    ? `Avg ${bh?.quantityAvg} · ${bh?.quantityStd}`
    : 'Source: VBAP.KWMENG · Orange line = rolling average';
  const dowTitle = 'Day-of-Week Demand Pattern';
  const dowSub = bh?.dow ? `Most frequent: ${bh.dowMode || '—'}` : 'Source: VBAK.ERDAT';
  const interChart = intervalHtml(realIa ?? p.demandCharts.interarrival);
  const qtyChart = qtyHtml(realQty ?? p.demandCharts.quantity);
  const dowChart = bh?.dow ? dowHtml(bh.dow, bh.dowMode) : '';
  // Distributions = real histograms of the per-pair interarrival/quantity arrays (fall back to the template
  // distributions only when the real arrays are absent), so they change with the selected scope/pair.
  const dists = p.mem?.distributions ?? [];
  const templateInterDists = dists.filter((d) => /interarrival|interval/i.test(d.label));
  const templateQtyDists = dists.filter((d) => !/interarrival|interval/i.test(d.label));
  const interDists = realIa ? [histDist('Interarrival (days)', realIa, 'd')] : templateInterDists;
  const qtyDists = realQty ? [histDist('Order quantity (units)', realQty, '')] : templateQtyDists;
  // Calendar has no per-pair real source → one of 4 deterministic mock variations by selection identity.
  const variationKey = scope === 'pair' && pairCust ? `${p.id}|${pairCust}` : p.id;
  const calData = CAL_VARIATIONS[hashKey(variationKey) % CAL_VARIATIONS.length];
  const calLegend =
    '<div style="display:flex;align-items:center;gap:5px;margin-top:8px">' +
    '<span style="font-size:9px;color:#9295A5">Less</span>' +
    [0.05, 0.2, 0.45, 0.7, 0.9]
      .map((o) => `<div style="width:9px;height:9px;border-radius:2px;background:rgba(39,68,131,${o})"></div>`)
      .join('') +
    '<span style="font-size:9px;color:#9295A5">More</span></div>';
  const calBody = `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;overflow-x:auto">${calHtml(calData)}${calLegend}</div>`;
  const chartCard = (title: string, sub: string, html: string) => (
    <div style={cardStyle}>
      <div
        style={{
          padding: '10px 14px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>{title}</div>
          <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>{sub}</div>
        </div>
        <ExpandButton onClick={() => open(title, sub, html)} />
      </div>
      <Raw style={{ padding: '12px 14px' }} html={html} />
    </div>
  );
  return (
    <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {node}
      {introBox(
        'Demand behaviors are aggregated across all customers ordering this SKU. Each signal has a confidence score based on data volume and pattern stability. These feed the BFG Foundation Model as product-level priors.',
        `Mined from SAP SD · ${p.ordersLTM} orders across ${p.customers.length} customer${p.customers.length > 1 ? 's' : ''}`,
      )}
      {/* Behavior graphs (top of tab): scope toggle + charts — reflect Overall vs a product×customer pair. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 3, background: C.warm, borderRadius: 8, padding: 3 }}>
          {(['overall', 'pair'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                padding: '4px 10px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                background: scope === s ? C.blue : 'transparent',
                color: scope === s ? '#fff' : C.t2,
              }}
            >
              {s === 'overall' ? 'Overall Demand Behavior' : 'By Customer'}
            </button>
          ))}
        </div>
        {scope === 'pair' && (
          <select
            value={pairCust}
            onChange={(e) => setPairCust(e.target.value)}
            style={{
              fontSize: 11,
              padding: '5px 9px',
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.t1,
              background: '#fff',
              maxWidth: 320,
            }}
          >
            <option value="">Select a customer…</option>
            {custOpts.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        )}
      </div>
      <div
        style={{
          fontSize: 10.5,
          color: C.t2,
          background: 'rgba(39,68,131,.05)',
          borderLeft: `3px solid ${C.blue}`,
          borderRadius: 6,
          padding: '6px 10px',
        }}
      >
        {scope === 'pair' && !pairCust ? 'Select a customer to see this SKU×customer demand behavior.' : scopeNote}
      </div>
      {noBehavior ? (
        <div
          style={{
            padding: '28px 20px',
            textAlign: 'center',
            background: C.warm,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
          }}
        >
          <div style={{ fontSize: 22, opacity: 0.25, marginBottom: 6 }}>📭</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.t2, marginBottom: 4 }}>No event behavior data</div>
          <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.5 }}>
            {pairActive
              ? `No recorded order events for this SKU × ${pairCustName}.`
              : 'No recorded order events for this product in the behavior feed.'}
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
            {calBody
              ? chartCard('Demand Calendar — Last 12 Months', 'Source: VBAK.ERDAT · Darker = more demand', calBody)
              : null}
            {dowChart ? chartCard(dowTitle, dowSub, dowChart) : null}
            {chartCard(interTitle, interSub, interChart)}
            {interDists.length
              ? chartCard(
                  'Interarrival Distribution',
                  'Shape shows predictability — tighter = more forecastable',
                  distHtml(interDists),
                )
              : null}
            {chartCard(qtyTitle, qtySub, qtyChart)}
            {qtyDists.length
              ? chartCard(
                  'Order Quantity Distribution',
                  'Histogram of order sizes — feeds quantity norm encoding',
                  distHtml(qtyDists),
                )
              : null}
          </div>
        </>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Demand Signals</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>
              Patterns extracted across all customers ordering this SKU
            </div>
          </div>
          <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {p.behaviors.map((b) => (
              <div key={b.name}>
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}
                >
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>{b.name}</div>
                    <div style={{ fontSize: 10, color: C.t3 }}>{b.val}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: cv(b.color) }}>{b.conf}%</div>
                    <div style={{ fontSize: 9, color: C.t3 }}>confidence</div>
                  </div>
                </div>
                <div style={{ height: 5, borderRadius: 3, background: C.border }}>
                  <div style={{ height: 5, borderRadius: 3, background: cv(b.color), width: `${b.conf}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Customer Mix</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>
              Who is ordering this product and their demand health
            </div>
          </div>
          <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {mixPageCount > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <button
                  type="button"
                  disabled={mixSafe <= 1}
                  onClick={() => setMixPage((m) => Math.max(1, m - 1))}
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: 6,
                    border: `1px solid ${C.border}`,
                    background: '#fff',
                    color: mixSafe <= 1 ? C.t3 : C.blue,
                    cursor: mixSafe <= 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  ‹ Prev
                </button>
                <span style={{ fontSize: 10, color: C.t2 }}>
                  {mixSafe} / {mixPageCount}
                </span>
                <button
                  type="button"
                  disabled={mixSafe >= mixPageCount}
                  onClick={() => setMixPage((m) => Math.min(mixPageCount, m + 1))}
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: 6,
                    border: `1px solid ${C.border}`,
                    background: '#fff',
                    color: mixSafe >= mixPageCount ? C.t3 : C.blue,
                    cursor: mixSafe >= mixPageCount ? 'not-allowed' : 'pointer',
                  }}
                >
                  Next ›
                </button>
              </div>
            )}
            {mixShown.map((cust) => (
              <div
                key={cust.custId || cust.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  background: C.warm,
                  borderRadius: 6,
                }}
              >
                <div style={{ flex: 1 }}>
                  {cust.custId ? (
                    <Link
                      to={`/data/customers?id=${encodeURIComponent(cust.custId)}`}
                      style={{ fontSize: 11.5, fontWeight: 700, color: C.blue, textDecoration: 'none' }}
                    >
                      {cust.name}
                    </Link>
                  ) : (
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: C.t1 }}>{cust.name}</div>
                  )}
                </div>
                {cust.memory != null && (
                  <div style={{ textAlign: 'right' }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: cust.memory >= 80 ? C.pos : cust.memory >= 60 ? C.warn : C.neg,
                      }}
                    >
                      {cust.memory}%
                    </div>
                    <div style={{ fontSize: 9, color: C.t3 }}>memory</div>
                  </div>
                )}
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: 100,
                    background: riskBg(cust.risk),
                    color: riskColor(cust.risk),
                  }}
                >
                  {cust.riskLabel ?? '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <BehavioralEncodingsDims profile={p.profile} bh={bh} />
    </div>
  );
}

/* ── Lifecycle tab ────────────────────────────────────────────────────── */
const PORT_AVG = [1, 21, 1, 1, 30];
function lifecycleP90(risk: string, i: number) {
  if (i === 0) return risk === 'ok' ? '2d' : '4d';
  if (i === 1) return risk === 'ok' ? '26d' : risk === 'warn' ? '32d' : '38d';
  if (i === 2) return risk === 'ok' ? '2d' : '3d';
  if (i === 3) return risk === 'ok' ? '2d' : risk === 'warn' ? '6d' : '10d';
  return risk === 'ok' ? '32d' : '38d';
}
function LifecycleTab({ p }: { p: Product }) {
  const hc = (s: string) => (s === 'ok' ? C.pos : s === 'warn' ? C.warn : C.neg);
  return (
    <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {introBox(
        'Median Δt at each lifecycle stage for this SKU, aggregated across all customers. Shows where friction typically occurs in the order-to-cash cycle for this product.',
        'Reconstructed from SAP SD · CDHDR/CDPOS · Aggregated across all customers',
      )}
      <div style={cardStyle}>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Order-to-Cash Lifecycle · {p.name}</div>
          <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>Median Δt per stage vs portfolio average</div>
        </div>
        <div style={{ padding: 16 }}>
          {p.lifecycle.map((s, i) => {
            const portP50 = `${PORT_AVG[i]}d`;
            const p50v = parseFloat(s.p50);
            const p90v = parseFloat(lifecycleP90(p.risk, i));
            const pp50v = PORT_AVG[i] || 1;
            const maxv = Math.max(p90v, pp50v) * 1.2;
            const better = p50v <= pp50v;
            return (
              <div key={s.stage} style={{ padding: '11px 0', borderTop: i > 0 ? `1px solid ${C.border}` : undefined }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 160, fontSize: 11, fontWeight: 600, color: C.t1, flexShrink: 0 }}>{s.stage}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: hc(s.health) }}>{s.p50}</div>
                      <div style={{ fontSize: 9.5, color: C.t3 }}>vs portfolio {portP50}</div>
                      <span style={{ fontSize: 9, fontWeight: 700, color: better ? C.pos : C.neg }}>
                        {better ? 'Better' : 'Slower'}
                      </span>
                    </div>
                    <div style={{ height: 5, borderRadius: 3, background: C.border }}>
                      <div
                        style={{
                          height: 5,
                          borderRadius: 3,
                          background: hc(s.health),
                          width: `${Math.min((p50v / maxv) * 100, 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      width: 130,
                      padding: '4px 8px',
                      background:
                        s.health === 'ok'
                          ? 'rgba(42,128,85,.05)'
                          : s.health === 'warn'
                            ? 'rgba(210,122,36,.05)'
                            : 'rgba(188,46,61,.05)',
                      borderRadius: 6,
                      borderLeft: `2px solid ${hc(s.health)}`,
                    }}
                  >
                    <div style={{ fontSize: 9.5, color: C.t2 }}>{s.note}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Patterns tab ─────────────────────────────────────────────────────── */
function PatternsTab({ p }: { p: Product }) {
  return (
    <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {introBox(
        'These patterns are matched at the SKU level across all customers. They are encoded as product priors in BFG and combined with customer-level behavioral encodings to produce forecasts.',
        'Product-level patterns · Feed BFG Foundation Model as priors',
      )}
      {p.patterns.map((pt) => (
        <div key={pt.name} style={{ ...cardStyle, padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.t1 }}>{pt.name}</div>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: '3px 10px',
                borderRadius: 100,
                background:
                  pt.conf >= 80 ? 'rgba(42,128,85,.1)' : pt.conf >= 60 ? 'rgba(210,122,36,.1)' : 'rgba(188,46,61,.1)',
                color: cv(pt.color),
              }}
            >
              {pt.conf}% confidence
            </span>
          </div>
          <div style={{ height: 4, borderRadius: 2, background: C.border, marginBottom: 10 }}>
            <div style={{ height: 4, borderRadius: 2, background: cv(pt.color), width: `${pt.conf}%` }} />
          </div>
          <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.6 }}>{pt.desc}</div>
          <div style={{ marginTop: 8 }}>
            <span
              style={{
                fontSize: 9.5,
                fontWeight: 600,
                color: C.blue,
                padding: '3px 10px',
                background: 'rgba(39,68,131,.06)',
                borderRadius: 6,
              }}
            >
              Used in BFG Foundation Model
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Economic Value tab ───────────────────────────────────────────────── */
function EconomicTab({ p }: { p: Product }) {
  const t = p.economic.revQ;
  const mx = Math.max(...t);
  const fin = [
    { l: 'Revenue YTD', v: p.revYTD, s: p.demandTrend + ' vs prior year', risk: false },
    { l: 'Open Order Value', v: p.economic.openOrderValue, s: 'Currently active', risk: false },
    {
      l: 'Revenue at Risk',
      v: p.economic.revenueAtRisk,
      s: 'Commit risk exposure',
      risk: p.economic.revenueAtRisk !== '$0',
    },
    { l: 'Gross Margin', v: p.economic.margin, s: 'Trailing 12 months', risk: false },
    { l: 'Forecast Error Cost', v: p.economic.forecastErrorCost, s: 'Per day of slip', risk: true },
    { l: 'SKU Rank', v: p.economic.skuRank, s: 'In portfolio', risk: false },
  ];
  return (
    <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {introBox(
        <>
          A 1-day forecast error for {p.name} costs approximately {p.economic.forecastErrorCost} in revenue exposure.
          The economic view quantifies what is at stake and informs how aggressively Keystone&apos;s Decisions Engine
          acts on signals for this product.
        </>,
        'Why forecast accuracy for this product matters',
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Revenue by Quarter</div>
            <div style={{ fontSize: 9.5, color: C.t3, marginTop: 1 }}>
              {p.demandTrend} trend · {p.revYTD} YTD
            </div>
          </div>
          <div style={{ padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 80, marginBottom: 6 }}>
              {t.map((v, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 3,
                    height: '100%',
                  }}
                >
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.t2 }}>${v}M</div>
                  <div
                    style={{
                      width: '100%',
                      borderRadius: '3px 3px 0 0',
                      background: i === t.length - 1 ? C.blue : 'rgba(39,68,131,.3)',
                      height: Math.round((v / mx) * 60),
                    }}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {p.economic.revLabels.map((l) => (
                <div key={l} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: C.t3 }}>
                  {l}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.t1 }}>Product Economics</div>
          </div>
          <div style={{ padding: '10px 14px' }}>
            {fin.map((m, i) => (
              <div
                key={m.l}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '7px 0',
                  borderTop: i > 0 ? `1px solid ${C.border}` : undefined,
                }}
              >
                <div style={{ fontSize: 11, color: C.t2 }}>{m.l}</div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: m.risk ? C.neg : C.t1 }}>{m.v}</div>
                  <div style={{ fontSize: 9.5, color: C.t3 }}>{m.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Tab bar ──────────────────────────────────────────────────────────── */
const TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'orders', label: 'Orders' },
  { key: 'demand', label: 'Demand Behavior' },
  { key: 'lifecycle', label: 'Lifecycle' },
  { key: 'patterns', label: 'Patterns' },
  { key: 'economic', label: 'Economic Value' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/* ── Page ─────────────────────────────────────────────────────────────── */
export function DataProducts() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Only honor a product id (guards against a stale ?id=cust_… leaking in from a customer link/nav,
  // which would otherwise 404 the describe call against /catalog/products/<cust_id>).
  const rawUrlId = searchParams.get('id');
  const urlId = rawUrlId && rawUrlId.startsWith('prod_') ? rawUrlId : null;
  // Selecting a product writes ?id=<productId> so each selection is a distinct, shareable URL.
  const selectProduct = (id: string) => {
    setSelectedId(id);
    setTab('summary');
    setSearchParams(
      (prev) => {
        prev.set('id', id);
        return prev;
      },
      { replace: true },
    );
  };
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('summary');
  const [search, setSearch] = useState('');
  // Initial list page from ?page= (1-based) so a paginated view is a shareable permalink. Coexists with ?id=.
  const urlPage = (() => {
    const n = parseInt(searchParams.get('page') ?? '', 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  })();
  const [page, setPage] = useState(urlPage);
  const PAGE_SIZE = 10;
  // Change the list page and sync ?page= (dropped at page 1 to keep URLs clean).
  const goToPage = (n: number) => {
    setPage(n);
    setSearchParams(
      (prev) => {
        if (n <= 1) prev.delete('page');
        else prev.set('page', String(n));
        return prev;
      },
      { replace: true },
    );
  };

  // Paginated product list straight from the catalog API (server pagination over 30k+ products).
  // isPlaceholderData is true while a new page is loading and the previous page is still shown — we use it to
  // swap the list for a spinner so paging gives clear loading feedback instead of stale rows.
  const { data, isPlaceholderData, isLoading } = useQuery<{
    products: Product[];
    total: number | null;
    hasNext: boolean;
  }>({
    queryKey: ['data-app', 'products', 'page', page, PAGE_SIZE],
    queryFn: () => fetch(`/api/data-app/products?page=${page}&pageSize=${PAGE_SIZE}`).then((r) => r.json()),
    placeholderData: (prev) => prev, // keep the current page visible while the next loads
  });

  const products = useMemo(() => data?.products ?? [], [data]);
  const total = data?.total ?? null;
  const pageCount = total != null ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : data?.hasNext ? page + 1 : page;
  const safePage = page;
  // Search filters the current page only (the list is server-paginated across all products).
  const paged = search ? products.filter((x) => x.name.toLowerCase().includes(search.toLowerCase())) : products;

  // Selection precedence: explicit click → ?id deep-link → first product of page 1 (don't auto-jump when paging).
  const effectiveId = selectedId ?? urlId ?? (page === 1 ? (products[0]?.id ?? null) : null);

  // Real identity for the selected product (Summary facts + header eyebrow).
  const { data: describe } = useQuery<ProductDescribe>({
    queryKey: ['data-app', 'products', effectiveId, 'describe'],
    queryFn: async () => {
      const r = await fetch(`/api/data-app/products/${encodeURIComponent(effectiveId!)}`);
      if (!r.ok) throw new Error('Failed to load product detail');
      return r.json();
    },
    enabled: !!effectiveId,
    staleTime: 5 * 60 * 1000,
  });

  // Lightweight existence gate fetched on selection (item-lifecycle limit 1 include_total). Cheap enough to run
  // on every select — including Summary — so all tabs know whether this SKU has any recorded demand at all.
  const { data: orderCount } = useQuery<{ hasOrders: boolean; total: number }>({
    queryKey: ['data-app', 'product', effectiveId, 'order-count'],
    queryFn: () =>
      fetch(`/api/data-app/product/${encodeURIComponent(effectiveId ?? '')}/order-count`).then((r) => r.json()),
    enabled: !!effectiveId,
    staleTime: 5 * 60 * 1000,
  });
  const ordersReady = orderCount !== undefined;
  const hasOrders = !!orderCount?.hasOrders;

  // On click into the Demand/Orders tabs: filtered item-lifecycle for this product → order rows + real summary
  // (customers / count / date). Deferred — the Summary tab never triggers the heavy events fetch.
  const { data: prodOrders } = useQuery<ProductOrdersData>({
    queryKey: ['data-app', 'product', effectiveId, 'orders'],
    queryFn: async () => {
      const r = await fetch(`/api/data-app/product/${encodeURIComponent(effectiveId!)}/orders`);
      if (!r.ok) throw new Error('Failed to load product orders');
      return r.json();
    },
    enabled: !!effectiveId && tab !== 'summary' && hasOrders,
    staleTime: 5 * 60 * 1000,
  });

  // Resolve the detail product: the selected product on the current page, else a template skeleton (e.g. a
  // deep-linked product on another page). Then overlay the real per-product summary (customers/orders/date).
  const found = products.find((x) => x.id === effectiveId) ?? null;
  const base =
    found ??
    (effectiveId && products[0]
      ? { ...products[0], id: effectiveId, name: describe?.name ?? effectiveId, customers: [], customerMix: [] }
      : null);
  const p = base
    ? prodOrders
      ? {
          ...base,
          // Real customers ordering this SKU, decorated with the template's mock memory/risk/type.
          customers: prodOrders.customers.map((c) => c.name),
          customerMix: prodOrders.customers.map((c, i) => {
            const dec = base.customerMix?.[i % (base.customerMix.length || 1)];
            return {
              custId: c.custId,
              name: c.name,
              type: dec?.type ?? null,
              memory: dec?.memory ?? null,
              risk: dec?.risk ?? null,
              riskLabel: dec?.riskLabel ?? null,
            };
          }),
          ordersLTM: prodOrders.ordersLTM,
          openOrders: prodOrders.ordersLTM,
          lastOrderIn: prodOrders.lastOrderIn || base.lastOrderIn,
        }
      : base
    : null;

  // Gate every detail tab on whether this SKU has any recorded demand. While the count is loading, show a quiet
  // placeholder; once known-empty, show a clear no-data message instead of rendering template/mock content.
  const orderGated = (node: React.ReactNode) =>
    !ordersReady ? (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 20px' }}>
        <Spinner size={26} />
      </div>
    ) : !hasOrders ? (
      <div style={{ padding: '48px 20px', textAlign: 'center' }}>
        <div style={{ fontSize: 30, opacity: 0.2, marginBottom: 8 }}>📭</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.t2, marginBottom: 4 }}>No Demand Behavior Data</div>
        <div style={{ fontSize: 11.5, color: C.t3 }}>This product has no recorded order events.</div>
      </div>
    ) : (
      node
    );

  return (
    <Page
      title="Products"
      subheader="Demand intelligence for each SKU — mined from your ERP transaction history. Click any product to see what Keystone has learned."
      fillHeight
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          height: '100%',
          background: '#fff',
          border: `1px solid ${C.border}`,
          borderRadius: 8,
        }}
      >
        {/* LEFT: list */}
        <div
          style={{
            width: 220,
            flexShrink: 0,
            borderRight: `1px solid ${C.border}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: C.warm,
          }}
        >
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}`, background: '#fff' }}>
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                goToPage(1);
              }}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: 11.5,
                color: C.t1,
                background: C.warm,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {/* Pagination (top, under search) — matches the Customers page. nowrap + flexShrink:0 keep the
                buttons + "x / y · 10/page" label on one line even at high page numbers in the 220px panel. */}
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, marginTop: 8 }}
            >
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() => goToPage(Math.max(1, safePage - 1))}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '3px 6px',
                  borderRadius: 6,
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  color: safePage <= 1 ? C.t3 : C.blue,
                  cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                ‹ Prev
              </button>
              <span style={{ fontSize: 10, color: C.t2, whiteSpace: 'nowrap' }}>
                {safePage} / {pageCount} <span style={{ color: C.t3 }}>· 10/page</span>
              </span>
              <button
                type="button"
                disabled={safePage >= pageCount}
                onClick={() => goToPage(Math.min(pageCount, safePage + 1))}
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '3px 6px',
                  borderRadius: 6,
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  color: safePage >= pageCount ? C.t3 : C.blue,
                  cursor: safePage >= pageCount ? 'not-allowed' : 'pointer',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                Next ›
              </button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {isPlaceholderData ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0' }}>
                <Spinner size={26} />
              </div>
            ) : (
              paged.map((x) => (
                <div
                  key={x.id}
                  onClick={() => selectProduct(x.id)}
                  style={{
                    padding: '9px 14px',
                    borderBottom: `1px solid ${C.border}`,
                    cursor: 'pointer',
                    background: effectiveId === x.id ? 'rgba(39,68,131,.08)' : '#fff',
                    borderLeft: effectiveId === x.id ? `3px solid ${C.blue}` : '3px solid transparent',
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.t1 }}>{x.name}</div>
                    <span style={{ fontSize: 9, fontWeight: 700, color: scoreColor(x.forecastScore) }}>
                      {x.forecastScore}%
                    </span>
                  </div>
                  <div style={{ fontSize: 9, color: C.t3, fontFamily: 'monospace', marginBottom: 3 }}>{x.id}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 600,
                        padding: '1px 6px',
                        borderRadius: 100,
                        background: riskBg(x.risk),
                        color: riskColor(x.risk),
                      }}
                    >
                      {x.riskLabel}
                    </span>
                    <span style={{ fontSize: 9, color: C.t3 }}>{x.demandTrend} YTD</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT: detail */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!p ? (
            isLoading || isPlaceholderData ? (
              // Deep-linked (?id=) and the list hasn't loaded yet — show a spinner, not the empty placeholder.
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner size={28} />
              </div>
            ) : (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  color: C.t3,
                }}
              >
                <div style={{ fontSize: 32, opacity: 0.2 }}>📦</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.t2 }}>Select a product</div>
                <div style={{ fontSize: 11, color: C.t3 }}>Click any product to see its demand profile</div>
              </div>
            )
          ) : (
            <>
              <div
                style={{
                  padding: '14px 20px',
                  borderBottom: `1px solid ${C.border}`,
                  background: '#fff',
                  flexShrink: 0,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.t3,
                        textTransform: 'uppercase',
                        letterSpacing: '.08em',
                        marginBottom: 3,
                      }}
                    >
                      {describe?.businessGroup || p.type}
                      {p.id ? (
                        <span style={{ color: C.t3, fontFamily: 'monospace', textTransform: 'none' }}>
                          {' · '}
                          {p.id}
                        </span>
                      ) : null}
                    </div>
                    <div
                      style={{ fontSize: 18, fontWeight: 700, color: C.t1, letterSpacing: '-0.02em', marginBottom: 2 }}
                    >
                      {p.name}
                    </div>
                    <div style={{ fontSize: 10.5, color: C.t3 }}>
                      {p.source} · {p.ordersLTM} orders LTM · {p.customers.length} customer
                      {p.customers.length > 1 ? 's' : ''}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: 100,
                      background: riskBg(p.risk),
                      color: riskColor(p.risk),
                      flexShrink: 0,
                    }}
                  >
                    {p.riskLabel}
                  </span>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  borderBottom: `1px solid ${C.border}`,
                  background: '#fff',
                  flexShrink: 0,
                  padding: '0 20px',
                }}
              >
                {TABS.map((tdef) => (
                  <div
                    key={tdef.key}
                    onClick={() => setTab(tdef.key)}
                    style={{
                      padding: '10px 14px',
                      fontSize: 11.5,
                      fontWeight: tab === tdef.key ? 700 : 500,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      color: tab === tdef.key ? C.blue : C.t3,
                      borderBottom: tab === tdef.key ? `2px solid ${C.blue}` : '2px solid transparent',
                      marginBottom: -1,
                    }}
                  >
                    {tdef.label}
                  </div>
                ))}
              </div>

              <div style={{ flex: 1, overflowY: 'auto', background: C.warm }}>
                {tab === 'summary' && orderGated(<SummaryTab p={p} describe={describe} />)}
                {tab === 'orders' && orderGated(effectiveId ? <OrdersTab p={p} productId={effectiveId} /> : null)}
                {tab === 'demand' && orderGated(<DemandTab key={p.id} p={p} />)}
                {tab === 'lifecycle' && orderGated(<LifecycleTab p={p} />)}
                {tab === 'patterns' && orderGated(<PatternsTab p={p} />)}
                {tab === 'economic' && orderGated(<EconomicTab p={p} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </Page>
  );
}
